import type { CollectionJSONObject, CollectionSummaryJSONObject } from '@ikegps/core-api';
import { ImageButton } from '../../components/button/image.button';
import { MaterialIcon } from '../../components/icon/icons';
import { ModalService } from '../../components/modal/modal.service';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { Spinner } from '../../components/spinner/spinner';
import { Table } from '../../components/table/table';
import { CSS as TableCSS } from '../../components/table/table.style';
import { addStyles } from '../../components/theme';
import { DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MChild, MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { IkePageName } from '../../page/page';
import { Services } from '../../services';
import { AppStore } from '../../store';
import { classNames } from '../../util/classnames';

const CSS = {
	container: 'c-CollectionHistoryTable',
	loading: 'c-CollectionHistoryTable--loading',
	tableCell: 'c-CollectionHistoryTable--tableCell',
	rowActions: 'c-CollectionHistoryTable--rowActions',
	revertButton: 'c-CollectionHistoryTable--revertButton',
	photoMeasure: 'c-CollectionHistoryTable--photoMeasure',
	titleBar: 'c-CollectionHistoryTable--titleBar',
	imageButton: 'c-CollectionHistoryTable--imageButton',
};

addStyles({
	[`${CSS.container} .${TableCSS.container}`]: {
		width: '80%',
	},
	[`${CSS.container} .${TableCSS.cell}`]: {
		padding: '0px 18px',
	},
	[`${CSS.container} .${TableCSS.body} th:first-of-type`]: {
		width: '132px',
	},
	[`${CSS.container} .${TableCSS.body} td:first-of-type`]: {
		width: '132px',
	},
	[`${CSS.container} .${TableCSS.body} th:last-of-type`]: {
		width: '150px',
	},
	[`${CSS.container} .${TableCSS.body} td:last-of-type`]: {
		width: '150px',
	},
	[CSS.loading]: {
		display: 'flex',
		'justify-content': 'center',
	},
	[CSS.tableCell]: {
		'text-overflow': 'ellipsis',
		overflow: 'hidden',
		'white-space': 'nowrap',
	},
	[`${TableCSS.row}:hover .${CSS.rowActions}`]: {
		visibility: 'visible',
	},
	[CSS.rowActions]: {
		display: 'flex',
		'justify-content': 'flex-end',
		visibility: 'hidden',
	},
	[CSS.revertButton]: {
		'margin-top': DefaultTheme.space(1),
	},
	[CSS.photoMeasure]: {
		transform: 'rotate(-90deg)',
	},
	[CSS.titleBar]: {
		display: 'flex',
		'justify-content': 'space-between',
		'align-items': 'center',
		width: '100%',
	},
	[CSS.imageButton]: {
		margin: DefaultTheme.space(1),
	},
});

const header = [
	Translate(T.REVISION_ID),
	Translate(T.MODIFIED_AT),
	Translate(T.MODIFIED_BY),
	Translate(T.MODIFICATIONS),
	'',
];

const cellView = (children?: MChild) =>
	m('span', { title: typeof children === 'string' ? children : undefined, className: CSS.tableCell }, children);

const photoMeasureLinkButton = (departmentId, tagId, collectionId) =>
	ImageButton.view(MaterialIcon.PHOTO_MEASURE, {
		title: T.LINK_PHOTOMEASURE,
		className: classNames(CSS.photoMeasure, CSS.imageButton),
		href: IkePageName.collectionMeasure,
		hrefParams: {
			t: tagId,
			d: departmentId,
			collection: collectionId,
		},
		disabled: false,
	});

const mapLinkButton = (tagId, collectionId) =>
	ImageButton.view(MaterialIcon.MAP, {
		title: T.LINK_MAP_WITH_COLLECTION,
		className: CSS.imageButton,
		href: IkePageName.map,
		hrefParams: {
			t: tagId,
			collection: collectionId,
		},
		disabled: false,
	});

const linkButtons = (departmentId, tagId, collectionId) =>
	m('div', [photoMeasureLinkButton(departmentId, tagId, collectionId), mapLinkButton(tagId, collectionId)]);

export const CollectionHistoryTable = {
	view: (
		collectionSummary: CollectionSummaryJSONObject,
		revisions: CollectionJSONObject[],
		userNames: { [userId: string]: string },
		modificationMessages: { [revision: string]: string }
	): MithrilVNode =>
		m('div', { className: CSS.container }, [
			Table.view(
				m('div', { className: CSS.titleBar }, [
					Translate(T.COLLECTION_REVISION_HISTORY_ID, collectionSummary.title),
					linkButtons(Services.Department.current.objectId, collectionSummary.tags[0], collectionSummary.uuid),
				]),
				header,
				revisions.map((r, i) => [
					cellView(r.revision),
					cellView(new Date(r.updatedAt).toUTCString()),
					cellView(userNames?.[r.updatedBy] ?? Spinner.view()),
					cellView(modificationMessages?.[r.revision] ?? Spinner.view()),
					m(
						'div',
						{ className: classNames(CSS.tableCell, CSS.rowActions) },
						i !== 0
							? m(
									'button',
									{
										className: 'mdl-button',
										onclick: async () => {
											const modalResult = await ModalService.open({
												title: Translate(T.COLLECTION_REVISION_HISTORY_MODAL_TITLE, {
													collectionName: collectionSummary.title,
													revisionId: r.revision,
												}),
												content: Translate(T.COLLECTION_REVISION_HISTORY_MODAL_DESCRIPTION),
												hasCancel: true,
												okName: T.COLLECTION_REVISION_HISTORY_MODAL_REVERT,
											});

											// Revert the collection!
											if (modalResult.ok && !modalResult.cancel) {
												const result = await Services.Department.rest.Collection.revert({
													collectionUuid: collectionSummary.uuid,
													revisionIdToRevertTo: r.revision,
												});

												if (result.results.collection) {
													NotificationService.success({
														key: T.COLLECTION_REVISION_HISTORY_MODAL_SUCCESS,
													});

													AppStore.TagSummary.clear();

													Services.Route.go(IkePageName.collectionMeasure, {
														d: Services.Department.current.objectId,
														t: result.results.collection.jobuuid,
														collection: collectionSummary.uuid,
													});
												} else {
													NotificationService.error({
														key: T.COLLECTION_REVISION_HISTORY_MODAL_FAILURE,
													});
												}
											}
										},
									},
									Translate(T.COLLECTION_REVISION_HISTORY_MODAL_REVERT)
								)
							: undefined
					),
				])
			),
		]),
};
